// ============================================================
//  Normalización: BacklogTask (crudo) -> NormalizedTask (derivado).
//  Centraliza derivaciones que no existen en la base (gaps G5, G6):
//  estado_entrega, solicitante/tipo_solicitante, días, horas_totales, es_ajuste.
// ============================================================
import { BacklogTask, Review } from './types';
import { EstadoEntrega, SLA_PROXIMO_VENCER_DIAS, esExcluido } from './config';
import { diaFecha, diaLocal, diaLocalDe, diasEntre } from './util';

const DAY = 86400000;

function toTime(iso?: string): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return isNaN(t) ? null : t;
}

/** Días enteros entre dos ISO; null si alguno es inválido. */
export function diffDays(aIso?: string, bIso?: string): number | null {
  const a = toTime(aIso);
  const b = toTime(bIso);
  if (a == null || b == null) return null;
  return Math.round((b - a) / DAY);
}

/** Fecha del pedido formal: fecha_solicitud, con fallback al timestamp de carga. */
export function reqDate(t: BacklogTask): string {
  return t.fecha_solicitud || t.created_at;
}

export type TipoSolicitante = 'espacio' | 'cliente_interno' | 'sede' | 'sin_definir';

/** Primer solicitante no vacío: espacio -> cliente interno -> sede (gap G6). */
export function solicitanteDe(t: BacklogTask): { solicitante: string; tipo: TipoSolicitante } {
  if (t.espacio) return { solicitante: t.espacio, tipo: 'espacio' };
  if (t.cliente_interno) return { solicitante: t.cliente_interno, tipo: 'cliente_interno' };
  if (t.sede) return { solicitante: t.sede, tipo: 'sede' };
  return { solicitante: '(sin solicitante)', tipo: 'sin_definir' };
}

/**
 * horas_totales del dashboard = SOLO horas reales cargadas (0 si no hay).
 * Política: a nivel de dashboard todas las horas son reales exclusivamente,
 * nunca se usan las estimadas como fallback.
 */
export function horasTotales(t: BacklogTask): number {
  const real = t.horas_reales === '' ? null : Number(t.horas_reales);
  return Number(real ?? 0) || 0;
}

/**
 * estado_entrega derivado (gap G5, METRICAS §6.5). `now` inyectable para tests.
 *
 * El deadline vale hasta el final de su día: una entrega en cualquier momento
 * de esa jornada cuenta a tiempo, y el vencimiento empieza recién al día
 * siguiente. Por eso se comparan días calendario y no instantes.
 *
 * Comparando instantes, el deadline valía hasta la medianoche en que ese día
 * *empieza*: todo lo entregado durante el día del deadline caía en 'tarde',
 * que es justo lo que pasa con los pedidos que se piden y se entregan el
 * mismo día.
 */
export function estadoEntrega(t: BacklogTask, now: Date = new Date()): EstadoEntrega {
  if (t.status === 'cancelled' || t.status === 'rejected' || t.status === 'pending_approval') {
    return 'no_aplica';
  }
  const deadline = diaFecha(t.deadline);
  const entrega = diaLocalDe(t.finished_at);
  const hoy = diaLocal(now);

  if (entrega) {
    if (!deadline) return 'a_tiempo'; // entregada sin deadline: no se penaliza
    if (entrega < deadline) return 'adelantada';
    if (entrega === deadline) return 'a_tiempo'; // el mismo día es a tiempo
    return 'tarde';
  }
  if (!deadline) return 'sin_deadline';
  if (hoy > deadline) return 'vencido';
  const faltan = diasEntre(hoy, deadline);
  if (faltan != null && faltan <= SLA_PROXIMO_VENCER_DIAS) return 'proximo';
  return 'en_curso';
}

export interface NormalizedTask {
  raw: BacklogTask;
  id: string;
  descripcion: string;
  titulo: string; // '' si la tarea es anterior al campo; cada tablero decide su fallback
  enlace_documentos: string;
  solicitante: string;
  tipo_solicitante: TipoSolicitante;
  espacio: string;
  cliente_interno: string;
  sede: string;
  direccion: string;
  proyecto: string;
  area_produccion: string; // categoria
  tipo_tarea: string; // pieza
  responsable: string; // assigned_to (email)
  estado: string; // status
  estado_entrega: EstadoEntrega;
  urgencia: string;
  dificultad: string;
  tipo: string; // Ordinaria / Extraordinaria
  es_urgente: boolean;
  es_extraordinaria: boolean;
  es_ajuste: boolean; // AJUSTE (cliente): la tarea es ajuste de otra anterior (campo ajuste no vacío)
  tiene_retrabajo: boolean; // RETRABAJO (interno): la tarea se reabrió al menos una vez (done -> activo)
  retrabajos_count: number; // veces que la tarea se reabrió (DB: ajustes_count, cuenta reaperturas)
  horas_totales: number;
  horas_estimadas: number;
  horas_reales: number;
  fecha_solicitud: string;
  fecha_inicio: string;
  deadline: string;
  fecha_entrega: string;
  dias_demora: number | null;
  dias_antelacion: number | null;
  dias_activo: number | null;
  dias_para_vencer: number | null;
  excluido: boolean;
  revision_status: string; // '' | 'pending' | 'disabled' | 'reviewed'
  revision_valoracion: string; // '' | 'aprobado' | 'rechazado' (solo si reviewed)
  revision_descripcion: string; // motivo/observación del cliente en la revisión
}

export function normalizeTask(
  t: BacklogTask,
  now: Date = new Date(),
  reviewByTask?: Record<string, Review>
): NormalizedTask {
  const { solicitante, tipo } = solicitanteDe(t);
  const ee = estadoEntrega(t, now);
  const req = reqDate(t);
  const nowIso = now.toISOString();
  const est = t.horas_estimadas === '' ? 0 : Number(t.horas_estimadas) || 0;
  const real = t.horas_reales === '' ? 0 : Number(t.horas_reales) || 0;
  const rev = reviewByTask?.[t.id];
  return {
    raw: t,
    id: t.id,
    descripcion: t.description,
    titulo: t.titulo,
    enlace_documentos: t.enlace_documentos,
    solicitante,
    tipo_solicitante: tipo,
    espacio: t.espacio,
    cliente_interno: t.cliente_interno,
    sede: t.sede,
    direccion: t.direccion,
    proyecto: t.proyecto,
    area_produccion: t.categoria,
    tipo_tarea: t.pieza,
    responsable: t.assigned_to,
    estado: t.status,
    estado_entrega: ee,
    urgencia: t.urgencia,
    dificultad: t.dificultad,
    tipo: t.tipo,
    es_urgente: t.urgencia === 'alta' || t.tipo === 'Extraordinaria',
    es_extraordinaria: t.tipo === 'Extraordinaria',
    es_ajuste: !!String(t.ajuste || '').trim(),
    tiene_retrabajo: (t.ajustes_count || 0) > 0,
    retrabajos_count: t.ajustes_count || 0,
    horas_totales: horasTotales(t),
    horas_estimadas: est,
    horas_reales: real,
    fecha_solicitud: req,
    fecha_inicio: t.start_date,
    deadline: t.deadline,
    fecha_entrega: t.finished_at,
    dias_demora: t.finished_at ? diffDays(req, t.finished_at) : null,
    dias_antelacion: t.deadline ? diffDays(req, t.deadline) : null,
    dias_activo: t.finished_at ? null : diffDays(req, nowIso),
    // En días calendario, como el vencimiento: 0 es "vence hoy", no "ya venció".
    dias_para_vencer: t.deadline ? diasEntre(diaLocal(now), diaFecha(t.deadline)) : null,
    excluido: esExcluido(t.status),
    revision_status: rev?.status || '',
    revision_valoracion: rev?.valoracion || '',
    revision_descripcion: rev?.descripcion || '',
  };
}

export function normalizeAll(
  tasks: BacklogTask[],
  now: Date = new Date(),
  reviewByTask?: Record<string, Review>
): NormalizedTask[] {
  return tasks.map((t) => normalizeTask(t, now, reviewByTask));
}

// ---- Calidad de datos (Tablero 8) ----
export type QualityKey =
  | 'sin_responsable'
  | 'sin_deadline'
  | 'sin_fecha_solicitud'
  | 'entregada_sin_fecha'
  | 'sin_area_produccion'
  | 'sin_solicitante'
  | 'horas_invalidas'
  | 'vencida_sin_deadline'
  | 'ajuste_sin_horas';

export const QUALITY_LABELS: Record<QualityKey, string> = {
  sin_responsable: 'Sin responsable asignado',
  sin_deadline: 'Sin deadline',
  sin_fecha_solicitud: 'Sin fecha de solicitud',
  entregada_sin_fecha: 'Finalizada sin fecha de entrega',
  sin_area_produccion: 'Sin área de producción',
  sin_solicitante: 'Sin espacio/cliente/sede',
  horas_invalidas: 'Horas nulas o negativas',
  vencida_sin_deadline: 'Activa sin deadline (no medible)',
  ajuste_sin_horas: 'Con retrabajo o ajuste pero sin horas cargadas',
};

export function qualityFlags(t: BacklogTask): QualityKey[] {
  const flags: QualityKey[] = [];
  if (esExcluido(t.status)) return flags; // no auditamos canceladas/rechazadas
  if (!t.assigned_to) flags.push('sin_responsable');
  if (!t.deadline) flags.push('sin_deadline');
  if (!t.fecha_solicitud && !t.created_at) flags.push('sin_fecha_solicitud');
  if (t.status === 'done' && !t.finished_at) flags.push('entregada_sin_fecha');
  if (!t.categoria) flags.push('sin_area_produccion');
  if (!t.espacio && !t.cliente_interno && !t.sede) flags.push('sin_solicitante');
  const real = t.horas_reales === '' ? null : Number(t.horas_reales);
  const est = t.horas_estimadas === '' ? null : Number(t.horas_estimadas);
  if ((real != null && real < 0) || (est != null && est < 0)) flags.push('horas_invalidas');
  if (['pending', 'in_progress', 'paused'].includes(t.status) && !t.deadline) {
    flags.push('vencida_sin_deadline');
  }
  if (((t.ajustes_count || 0) > 0 || !!String(t.ajuste || '').trim()) && horasTotales(t) === 0) flags.push('ajuste_sin_horas');
  return flags;
}
